import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { LocalCacheService } from '../services/local-cache.service';
import { TokenRefreshService } from '../services/token-refresh.service';
import { SKIP_AUTH } from './http-context-tokens';
import { environment } from '../../../environments/environment';

const isAuthApi = (url: string): boolean =>
  url.includes('/auth/') || url.startsWith(environment.authApiOrigin);

/** Never refresh for these: a 401 from login means bad credentials, and refresh must not refresh itself. */
const isLoginOrRefresh = (url: string): boolean =>
  /\/auth\/(login|refresh-token)(\?|$)/.test(url);

/**
 * Adds the Bearer token and, on a 401, refreshes it once and resends the request (Task 25762).
 *
 * - Parallel 401s share one refresh call (TokenRefreshService is single flight).
 * - Only requests this interceptor signed are refreshed. A request that set its own Authorization header (SSO
 *   validation, logout, change password) is left alone, as before.
 * - The refresh service ends the session only when the refresh token is dead. A 5xx or network error from the
 *   refresh is returned to the caller and the session is kept.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(SKIP_AUTH)) {
    return next(req);
  }

  const cache = inject(LocalCacheService);
  const router = inject(Router);
  const refresher = inject(TokenRefreshService);

  // Don't overwrite if Authorization header is already set (e.g., SSO validation)
  const hasAuthHeader = req.headers.has('Authorization');
  const token = cache.authToken;
  const signedHere = !!token && !hasAuthHeader;

  const authReq = signedHere ? withBearer(req, token) : req;

  // Before Task 25762 this was the only 401 handling: 401 from our auth API -> clear and sign in. Other services
  // may return 401 for different reasons (e.g., missing API key), so their 401 is just passed on.
  const handleFinal401 = (url: string) => {
    if (isAuthApi(url)) {
      cache.clearAuthData();
      router.navigateByUrl('/sign-in');
    }
  };

  return next(authReq).pipe(
    catchError((err) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }

      if (signedHere && !isLoginOrRefresh(req.url)) {
        if (cache.refreshToken) {
          return refresher.refresh().pipe(
            switchMap((newToken) =>
              next(withBearer(req, newToken)).pipe(
                catchError((retryErr) => {
                  if (retryErr instanceof HttpErrorResponse && retryErr.status === 401) {
                    handleFinal401(req.url);
                  }
                  return throwError(() => retryErr);
                }),
              ),
            ),
          );
        }

        // No refresh token of our own (for example a session that came from the Web SSO hand-off) and the token
        // has expired: the session is over, so go to sign-in instead of failing every call.
        if (token && cache.isTokenExpired(token)) {
          cache.clearAuthData();
          router.navigateByUrl('/sign-in');
          return throwError(() => err);
        }
      }

      handleFinal401(req.url);
      return throwError(() => err);
    }),
  );
};

function withBearer<T>(req: HttpRequest<T>, token: string): HttpRequest<T> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}
